import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Displays a single, cart-wide progress bar + dynamic upsell message,
 * showing progress toward the universal volume-pricing tiers based on the
 * total bottle count across all products in the cart. Updates live as any
 * line's quantity changes and after cart updates.
 *
 * @typedef {Object} VolumeTier
 * @property {number} quantity - Minimum quantity for this tier
 * @property {string} discount - Formatted discount amount per bottle at this tier
 *
 * @typedef {Object} CartVolumeUpsellRefs
 * @property {HTMLElement} [cartVolumeUpsellFill] - The progress bar fill element
 * @property {HTMLElement[]} [cartVolumeUpsellDots] - The tier marker dots
 * @property {HTMLElement} [cartVolumeUpsellText] - The text element displaying the upsell message
 *
 * @extends {Component<CartVolumeUpsellRefs>}
 */
class CartVolumeUpsellComponent extends Component {
  /** @type {VolumeTier[]} */
  #tiers = [];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#parseTiers();
    this.#attachEventListeners();
    this.#updateDisplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #parseTiers() {
    const { tiers: tiersData } = this.dataset;

    if (tiersData) {
      const tiers = JSON.parse(tiersData);
      for (const { quantity, discount } of tiers) {
        if (quantity && discount) {
          this.#tiers.push({ quantity: parseInt(quantity), discount });
        }
      }
    }

    // Ascending order: tiers[0] is the first (lowest) tier, etc.
    this.#tiers.sort((a, b) => a.quantity - b.quantity);
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleUpdate, { signal });
  }

  #handleUpdate = () => {
    this.#updateDisplay();
  };

  /**
   * Total bottle count across every cart line (sums every quantity input on
   * the page, so it stays accurate even if the cart is rendered in several
   * places at once, e.g. drawer + page).
   */
  #getCurrentQuantity() {
    const inputs = document.querySelectorAll('input[name="updates[]"]');
    let total = 0;
    for (const input of inputs) {
      total += parseInt(/** @type {HTMLInputElement} */ (input).value || '0') || 0;
    }
    return total;
  }

  updateDisplay() {
    const segmentCount = this.#tiers.length;
    if (!segmentCount) return;

    const quantity = this.#getCurrentQuantity();
    const nextTierIndex = this.#tiers.findIndex((tier) => quantity < tier.quantity);
    const nextTier = nextTierIndex === -1 ? null : this.#tiers[nextTierIndex];
    const lowerBound = nextTierIndex <= 0 ? 0 : this.#tiers[nextTierIndex - 1].quantity;

    // Palier maximum atteint : plus rien à afficher.
    this.hidden = !nextTier;
    if (!nextTier) return;

    const span = nextTier.quantity - lowerBound;
    const progressed = Math.max(0, quantity - lowerBound);
    const fillWithinPercent = span > 0 ? (progressed / span) * 100 : 0;
    const overallPercent = (nextTierIndex * 100) / segmentCount + fillWithinPercent / segmentCount;

    const fillEl = this.refs.cartVolumeUpsellFill;
    if (fillEl) fillEl.style.width = `${overallPercent}%`;

    const dots = this.refs.cartVolumeUpsellDots ?? [];
    dots.forEach((dot, index) => {
      dot.toggleAttribute('data-reached', index < nextTierIndex);
    });

    const textEl = this.refs.cartVolumeUpsellText;
    if (textEl) {
      const gap = nextTier.quantity - quantity;
      const unit = gap > 1 ? this.dataset.unitPlural : this.dataset.unitSingular;
      textEl.innerHTML = `${this.dataset.beforeText}${gap} ${unit}${this.dataset.middleText}${nextTier.discount}${this.dataset.afterText}`;
    }
  }

  #updateDisplay = () => {
    this.updateDisplay();
  };
}

if (!customElements.get('cart-volume-upsell')) {
  customElements.define('cart-volume-upsell', CartVolumeUpsellComponent);
}
